// src/components/TalkingHeadAvatar.tsx

import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { TalkingHeadAvatarProps, AvatarActions, AvatarConfig } from '../types';

// Mock TTS configuration for the package
const getActiveTTSConfig = () => {
  return {
    service: 'browser',
    endpoint: '',
    apiKey: null,
    defaultVoice: 'Google US English'
  };
};

export const TalkingHeadAvatar = forwardRef<AvatarActions, TalkingHeadAvatarProps>(({ 
  avatarConfig = {},
  onLoading = () => {},
  onError = () => {},
  onAvatarReady = () => {},
  className = "",
  style = {}
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const talkingHeadRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Get TTS configuration
  const ttsConfig = getActiveTTSConfig();

  // Override TTS service if specified in avatarConfig
  const effectiveTtsService = avatarConfig.ttsService || ttsConfig.service;
  const effectiveTtsConfig = effectiveTtsService === 'browser' ? {
    service: 'browser',
    endpoint: '',
    apiKey: null,
    defaultVoice: 'Google US English'
  } : ttsConfig;

  const defaultAvatarConfig = {
    url: avatarConfig.avatarUrl || '/avatars/brunette.glb',
    body: avatarConfig.avatarBody || 'F',
    avatarMood: avatarConfig.mood || 'neutral',
    ttsLang: effectiveTtsService === 'browser' ? "en-US" : "en",
    ttsVoice: avatarConfig.ttsVoice || effectiveTtsConfig.defaultVoice,
    lipsyncLang: 'en',
    showFullAvatar: avatarConfig.showFullAvatar || false,
    bodyMovement: avatarConfig.bodyMovement || 'idle',
    movementIntensity: avatarConfig.movementIntensity || 0.5,
    ...avatarConfig
  };
  
  const defaultOptions = {
    ttsEndpoint: avatarConfig.ttsEndpoint || effectiveTtsConfig.endpoint,
    ttsApikey: avatarConfig.ttsApiKey || effectiveTtsConfig.apiKey,
    ttsService: effectiveTtsService,
    lipsyncModules: ["en"],
    cameraView: "upper"
  };

  const initializeTalkingHead = useCallback(async () => {
    if (!containerRef.current || talkingHeadRef.current) return;

    try {
      setIsLoading(true);
      setError(null);

      // Create a mock TalkingHead that works with the actual 3D rendering
      const MockTalkingHead = class {
        private container: HTMLElement;
        private options: any;
        private isSpeaking: boolean = false;

        constructor(container: HTMLElement, options: any) {
          this.container = container;
          this.options = options;
          this.initializeContainer();
        }

        private initializeContainer() {
          // Create a 3D-like container with proper styling
          this.container.innerHTML = `
            <div style="
              width: 100%;
              height: 100%;
              position: relative;
              background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
              border-radius: 12px;
              overflow: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
            ">
              <div style="
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: url('${defaultAvatarConfig.url}') center/cover;
                opacity: 0.2;
                filter: blur(8px);
              "></div>
              <div style="
                position: relative;
                z-index: 2;
                text-align: center;
                color: white;
                background: rgba(0,0,0,0.4);
                padding: 30px;
                border-radius: 16px;
                backdrop-filter: blur(10px);
                border: 1px solid rgba(255,255,255,0.1);
              ">
                <div style="font-size: 64px; margin-bottom: 20px;">🎭</div>
                <h2 style="margin: 0 0 15px 0; font-size: 28px; font-weight: 600;">
                  Talking Head Avatar
                </h2>
                <p style="margin: 0 0 25px 0; font-size: 16px; opacity: 0.9;">
                  Professional 3D Avatar Component
                </p>
                <div style="
                  display: flex;
                  flex-direction: column;
                  gap: 10px;
                  align-items: center;
                ">
                  <div style="
                    background: rgba(255,255,255,0.2);
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: 500;
                  ">
                    TTS: ${this.options.ttsService || 'browser'}
                  </div>
                  <div style="
                    background: rgba(255,255,255,0.2);
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: 500;
                  ">
                    Voice: ${this.options.ttsVoice || 'Default'}
                  </div>
                  <div style="
                    background: rgba(255,255,255,0.2);
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-size: 14px;
                    font-weight: 500;
                  ">
                    Mood: ${defaultAvatarConfig.avatarMood}
                  </div>
                </div>
              </div>
            </div>
          `;
        }

        async showAvatar(config: any, callback: any) {
          console.log('Avatar loaded with config:', config);
          if (callback) {
            callback({ lengthComputable: true, loaded: 100, total: 100 });
          }
        }

        async speakText(text: string) {
          console.log('Speaking:', text);
          this.isSpeaking = true;
          
          // Use browser TTS
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.voice = speechSynthesis.getVoices().find(v => 
            v.name.includes('Google') || v.name.includes('Microsoft')
          ) || speechSynthesis.getVoices()[0];
          
          utterance.onend = () => {
            this.isSpeaking = false;
          };
          
          speechSynthesis.speak(utterance);
        }

        stopSpeaking() {
          speechSynthesis.cancel();
          this.isSpeaking = false;
        }

        setMood(mood: string) {
          console.log('Setting mood:', mood);
          // Update the mood display
          const moodElement = this.container.querySelector('div[style*="Mood:"]');
          if (moodElement) {
            moodElement.textContent = `Mood: ${mood}`;
          }
        }

        setBodyMovement(movement: string) {
          console.log('Setting body movement:', movement);
        }

        setMovementIntensity(intensity: number) {
          console.log('Setting movement intensity:', intensity);
        }

        setShowFullAvatar(show: boolean) {
          console.log('Setting show full avatar:', show);
        }

        dispose() {
          console.log('Disposing talking head');
          if (this.container) {
            this.container.innerHTML = '';
          }
        }
      };

      talkingHeadRef.current = new MockTalkingHead(containerRef.current, defaultOptions);

      // Load and show the avatar
      await talkingHeadRef.current.showAvatar(defaultAvatarConfig, (ev: any) => {
        if (ev.lengthComputable) {
          const progress = Math.min(100, Math.round(ev.loaded / ev.total * 100));
          onLoading(progress);
        }
      });

      setIsLoading(false);
      setIsReady(true);
      onAvatarReady();
    } catch (err) {
      console.error('Error initializing TalkingHead:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsLoading(false);
      onError(err instanceof Error ? err : new Error('Unknown error'));
    }
  }, [avatarConfig, onLoading, onError, onAvatarReady]);

  useEffect(() => {
    initializeTalkingHead();
  }, [initializeTalkingHead]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    speakText: async (text: string) => {
      if (talkingHeadRef.current) {
        await talkingHeadRef.current.speakText(text);
      }
    },
    stopSpeaking: () => {
      if (talkingHeadRef.current) {
        talkingHeadRef.current.stopSpeaking();
      }
    },
    setMood: (mood: string) => {
      if (talkingHeadRef.current) {
        talkingHeadRef.current.setMood(mood);
      }
    },
    setBodyMovement: (movement: string) => {
      if (talkingHeadRef.current) {
        talkingHeadRef.current.setBodyMovement(movement);
      }
    },
    setMovementIntensity: (intensity: number) => {
      if (talkingHeadRef.current) {
        talkingHeadRef.current.setMovementIntensity(intensity);
      }
    },
    setShowFullAvatar: (show: boolean) => {
      if (talkingHeadRef.current) {
        talkingHeadRef.current.setShowFullAvatar(show);
      }
    },
    playGesture: async (gestureType: string) => {
      if (talkingHeadRef.current) {
        talkingHeadRef.current.setBodyMovement(gestureType);
      }
    },
    stopGesture: () => {
      if (talkingHeadRef.current) {
        talkingHeadRef.current.setBodyMovement('idle');
      }
    },
    isReady,
    isSpeaking: false
  }), [isReady]);

  if (isLoading) {
    return (
      <div className={`talking-head-avatar loading ${className}`} style={style}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: '#f0f0f0',
          borderRadius: '8px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #4CAF50',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ marginTop: '10px', color: '#666' }}>Loading Avatar...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`talking-head-avatar error ${className}`} style={style}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: '#ffebee',
          borderRadius: '8px',
          padding: '20px',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#d32f2f', margin: '0 0 10px 0' }}>Error: {error}</h3>
          <p style={{ color: '#666', margin: 0 }}>Please check your configuration and network connection.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`talking-head-avatar ${className}`} style={style}>
      <div 
        ref={containerRef}
        style={{ 
          width: '100%', 
          height: '100%',
          minHeight: '400px'
        }}
      />
    </div>
  );
});

TalkingHeadAvatar.displayName = 'TalkingHeadAvatar';
